/**
 * EmailDiagnosticsPage
 *
 * Admin-Tool zur Diagnose des Fehlers "Diese E-Mail-Adresse wird bereits
 * verwendet". Sucht eine E-Mail global in ALLEN MongoDB-Collections und
 * zeigt jeden Treffer mit Collection, ID, Feld und Kontext-Info.
 *
 * Backend: GET /api/admin/diagnostics/find-email?email=...
 */
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Search, Mail, AlertCircle, CheckCircle2, Database, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailDiagnosticsPage() {
  const [email, setEmail] = useState('');
  const [includeLogs, setIncludeLogs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!email.trim() || !email.includes('@')) {
      setError('Bitte eine gültige E-Mail eingeben.');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API}/admin/diagnostics/find-email`, {
        params: { email: email.trim(), include_logs: includeLogs },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(resp.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="email-diagnostics-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Mail className="h-6 w-6 text-orange-600" />
          E-Mail-Diagnose
        </h1>
        <p className="text-gray-600 mt-1">
          Findet heraus, wo eine E-Mail-Adresse im System gespeichert ist —
          nützlich beim Fehler &quot;wird bereits verwendet&quot;.
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" />
            E-Mail suchen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="email-input">E-Mail-Adresse</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="email-input"
                data-testid="email-diagnostics-input"
                type="email"
                placeholder="z.B. trebolcore@hotmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button
                onClick={handleSearch}
                disabled={loading}
                data-testid="email-diagnostics-search-btn"
                className="bg-orange-600 hover:bg-orange-700"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Suchen
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="include-logs"
              checked={includeLogs}
              onCheckedChange={setIncludeLogs}
              data-testid="email-diagnostics-include-logs"
            />
            <Label htmlFor="include-logs" className="text-sm text-gray-600">
              Logs/Audit-Trails ebenfalls durchsuchen (langsamer)
            </Label>
          </div>

          {error && (
            <Alert variant="destructive" data-testid="email-diagnostics-error">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card data-testid="email-diagnostics-result">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                {result.total_matches > 0 ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-gray-400" />
                )}
                Ergebnisse für: <code className="text-blue-700">{result.email_searched}</code>
              </span>
              <div className="flex gap-2">
                <Badge variant="outline">
                  <Database className="h-3 w-3 mr-1" />
                  {result.collections_scanned} Collections geprüft
                </Badge>
                <Badge variant={result.total_matches > 0 ? 'destructive' : 'secondary'}>
                  {result.total_matches} Treffer
                </Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result.total_matches === 0 ? (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Diese E-Mail-Adresse wurde in keiner Collection gefunden.
                  Wenn der Mobile-Endpoint trotzdem &quot;bereits verwendet&quot;
                  meldet, könnte ein Caching-Problem oder eine andere DB-Instanz
                  verantwortlich sein.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                {result.matches.map((m, i) => (
                  <div
                    key={i}
                    className="border rounded-lg p-4 bg-gray-50"
                    data-testid={`email-diagnostics-match-${i}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="default" className="bg-blue-600">
                          {m.collection}
                        </Badge>
                        {m.deleted && (
                          <Badge variant="destructive">soft-deleted</Badge>
                        )}
                        {m.status && (
                          <Badge variant="outline">{m.status}</Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 font-mono">
                        {m.id}
                      </span>
                    </div>

                    <div className="text-sm space-y-1">
                      {Object.entries(m.matched_fields || {}).map(([f, v]) => (
                        <div key={f} className="flex gap-2">
                          <span className="font-mono text-orange-700 text-xs">
                            {f}:
                          </span>
                          <span className="text-gray-900">{v}</span>
                        </div>
                      ))}
                      {(m.first_name || m.last_name || m.name) && (
                        <div className="flex gap-2 pt-1">
                          <span className="text-xs text-gray-500">Name:</span>
                          <span className="text-gray-700">
                            {[m.first_name, m.last_name, m.name].filter(Boolean).join(' ')}
                          </span>
                        </div>
                      )}
                      {m.created_at && (
                        <div className="flex gap-2">
                          <span className="text-xs text-gray-500">Erstellt:</span>
                          <span className="text-gray-700 text-xs">
                            {new Date(m.created_at).toLocaleString('de-DE')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {result.skipped?.length > 0 && (
              <details className="mt-4 text-xs text-gray-500">
                <summary className="cursor-pointer">
                  {result.skipped.length} Collections übersprungen (Fehler)
                </summary>
                <ul className="mt-1 pl-4 list-disc">
                  {result.skipped.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </details>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
